import { memo, useCallback } from 'react';

import { type AirActionTrackingLocation } from '@air/analytics';
import { type AssetCustomFieldValue } from '@air/api/types';

import {
  CustomFieldPlainText,
  type CustomFieldPlainTextProps,
} from '~/components/CustomFields/CustomFieldInput/CustomFieldPlainText';
import { useUpdatePublicItemsCustomFieldValue } from '~/utils/CustomFields/useUpdatePublicItemsCustomFieldValue';

export type PublicAssetCustomFieldPlainTextProps = {
  field: AssetCustomFieldValue;
  trackingLocation: AirActionTrackingLocation;
  canEditCustomFields: boolean;
  assetId: string;
} & Omit<CustomFieldPlainTextProps, 'onSubmit'>;

export const PublicAssetCustomFieldPlainText = memo(
  ({ field, trackingLocation, canEditCustomFields, assetId, ...rest }: PublicAssetCustomFieldPlainTextProps) => {
    const { updatePublicItemsCustomFieldValue } = useUpdatePublicItemsCustomFieldValue();

    const onSubmit = useCallback(
      async (value: string) => {
        if (!assetId) return;
        await updatePublicItemsCustomFieldValue({
          assetIds: [assetId],
          baseCustomField: field,
          nextPlainTextValue: value,
          trackingLocation,
        });
      },
      [assetId, updatePublicItemsCustomFieldValue, field, trackingLocation],
    );

    return <CustomFieldPlainText {...rest} onSubmit={canEditCustomFields ? onSubmit : undefined} field={field} />;
  },
);

PublicAssetCustomFieldPlainText.displayName = 'PublicAssetCustomFieldPlainText';
